import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { PAINEL_URL } from '../../../config'
import { useHasAccess } from '../../../hooks/auth'
import { useOsList } from '../../../hooks/os'
import { OsListFilters, OsListRow, OS_PERM } from '../../../types/os'
import { useImportBatch } from '../ImportBatchContext'
import {
  BD,
  Button,
  Card,
  Field,
  FiltersRow,
  Input,
  KpiCard,
  KpiGrid,
  PageSubtitle,
  PageTitle,
  Pill,
  Select,
  Spinner,
  StatusBadge,
  Table,
} from '../styles'
import { fmtDate, fmtMoney, FECHAMENTO_UI, STATUS_UI } from '../statusUi'
import { LOCAL_EXECUTION } from '../../../utils/featureFlag'

interface BarProps {
  label: string
  value: number
  total: number
  color: string
  onClick?: () => void
}
const Bar: FC<BarProps> = ({ label, value, total, color, onClick }) => {
  const pct = total ? Math.round((value / total) * 100) : 0
  return (
    <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default', marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span style={{ color: BD.grayText }}>{label}</span>
        <span style={{ fontWeight: 600 }}>{value}</span>
      </div>
      <div style={{ width: '100%', height: 8, background: '#E5E7EB', borderRadius: 9999 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 9999 }} />
      </div>
    </div>
  )
}

interface SortableThProps {
  col: string
  sortBy?: string
  sortDir?: string
  onSort: (col: string) => void
  alignRight?: boolean
  children: ReactNode
}
const SortableTh: FC<SortableThProps> = ({ col, sortBy, sortDir, onSort, alignRight, children }) => {
  const active = sortBy === col
  return (
    <th
      onClick={() => onSort(col)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textAlign: alignRight ? 'right' : undefined }}
    >
      {children}
      <span style={{ marginLeft: 4, fontSize: 9, opacity: active ? 1 : 0.35 }}>
        {active ? (sortDir === 'desc' ? '▼' : '▲') : '▲▼'}
      </span>
    </th>
  )
}

/** Intervalo de uma semana (seg–dom) deslocada por `offset` semanas, em 'YYYY-MM-DD'. */
const semanaRange = (offset = 0): { dataIni: string; dataFim: string } => {
  const hoje = new Date()
  const dia = hoje.getDay() // 0=dom ... 6=sáb
  const ateSegunda = dia === 0 ? -6 : 1 - dia
  const segunda = new Date(hoje)
  segunda.setDate(hoje.getDate() + ateSegunda + offset * 7)
  const domingo = new Date(segunda)
  domingo.setDate(segunda.getDate() + 6)
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { dataIni: fmt(segunda), dataFim: fmt(domingo) }
}

// Persistência dos filtros entre idas/voltas da listagem (sessão do navegador).
const FILTERS_KEY = 'os-list-filters'
const loadFilters = (): OsListFilters | null => {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY)
    return raw ? (JSON.parse(raw) as OsListFilters) : null
  } catch {
    return null
  }
}

/** Link direto pro fechamento do evento no painel2 (P2). */
const p2FechamentoUrl = (eventoId: number): string =>
  `${(PAINEL_URL || '').replace(/\/$/, '')}/painel/fechamento/cadastro/${eventoId}`

export const OsList: FC = () => {
  const navigate = useNavigate()
  // Filtro inicial: o persistido (volta da OS mantém o filtro) ou, na 1ª vez, a semana atual.
  const inicial = useMemo<OsListFilters>(() => loadFilters() ?? { ...semanaRange(0), page: 1 }, [])
  // draft = valores digitados nos filtros; applied = o que é enviado à API.
  const [draft, setDraft] = useState<OsListFilters>(inicial)
  const [applied, setApplied] = useState<OsListFilters>(inicial)
  const { rows, kpis, charts, lastPage, page, pracas, isLoading, isError, isUnauthorized } = useOsList(applied)

  // Persiste o filtro aplicado pra restaurar ao voltar pra listagem.
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTERS_KEY, JSON.stringify(applied))
    } catch {
      /* sessionStorage indisponível — ignora */
    }
  }, [applied])

  const canImport = useHasAccess(OS_PERM.IMPORTAR)
  const { startBatch } = useImportBatch()
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Só OS aprovadas e ainda não importadas podem entrar no lote.
  const importavel = (os: OsListRow) => os.status === 'APROVADA' && os.importStatus !== 'IMPORTADA'
  const selecionaveis = rows.filter(importavel)
  const allSel = selecionaveis.length > 0 && selecionaveis.every((r) => selected.has(r.id))
  const toggleAll = () =>
    setSelected(allSel ? new Set() : new Set(selecionaveis.map((r) => r.id)))
  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const selecionadas = rows.filter((r) => selected.has(r.id))
  const totalSel = selecionadas.reduce((s, r) => s + (Number(r.custoTotal) || 0), 0)
  const cols = canImport ? 13 : 12
  const iniciarImportacao = () => {
    startBatch(
      selecionadas.map((r) => ({
        osId: r.id,
        numero: r.numero,
        evento: r.evento ?? '—',
        produtor: r.produtor ?? '—',
        total: Number(r.custoTotal) || 0,
      })),
    )
    setConfirmOpen(false)
    navigate('/interno/os/importacao')
  }

  const setD = (key: keyof OsListFilters, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value || undefined }))

  const aplicar = () => setApplied({ ...draft, page: 1 })
  const limpar = () => {
    setDraft({})
    setApplied({})
  }
  // Gráficos aplicam direto (e refletem no dropdown)
  const aplicarRapido = (key: keyof OsListFilters, value: string) => {
    setDraft((d) => ({ ...d, [key]: value }))
    setApplied((a) => ({ ...a, [key]: value, page: 1 }))
  }
  // Atalhos de semana (esta / anterior): aplicam o intervalo de datas direto.
  const aplicarSemana = (offset: number) => {
    const r = semanaRange(offset)
    setDraft((d) => ({ ...d, ...r }))
    setApplied((a) => ({ ...a, ...r, page: 1 }))
  }
  const ordenar = (col: string) => {
    const dir = applied.sortBy === col && applied.sortDir !== 'desc' ? 'desc' : 'asc'
    setDraft((d) => ({ ...d, sortBy: col, sortDir: dir }))
    setApplied((a) => ({ ...a, sortBy: col, sortDir: dir, page: 1 }))
  }
  const irPagina = (p: number) => setApplied((a) => ({ ...a, page: p }))
  const paginaAtual = page ?? 1

  // Lista completa vinda da API (independe da página/filtros atuais). O fallback
  // pelas linhas cobre o intervalo até o backend com `pracas` estar no ar.
  const pracaOptions = useMemo(() => {
    if (pracas.length) return pracas.map((p) => [p.id, p.nome] as const)
    const map = new Map<number, string>()
    rows.forEach((r) => {
      if (r.pracaId && r.praca) map.set(r.pracaId, r.praca)
    })
    return Array.from(map.entries())
  }, [pracas, rows])

  // Backend negou por sessão vencida (rede de segurança do OP-3462): o /auth
  // valida e redireciona pelo fluxo normal do app — nada de window.location.
  if (isUnauthorized && !LOCAL_EXECUTION) {
    return <Navigate to="/auth" replace />
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>Ordens de Serviço</PageTitle>
          <PageSubtitle>Controle de custos operacionais dos eventos</PageSubtitle>
        </div>
        {canImport && (
          <Button disabled={selected.size === 0} onClick={() => setConfirmOpen(true)}>
            Importar OS
            {selected.size > 0 && (
              <span style={{ marginLeft: 8, background: 'rgba(255,255,255,0.25)', borderRadius: 9999, padding: '1px 8px', fontSize: 12 }}>
                {selected.size}
              </span>
            )}
          </Button>
        )}
      </div>

      <KpiGrid>
        <KpiCard><div className="kpi-label">Total de OS</div><div className="kpi-value">{kpis?.total ?? '—'}</div></KpiCard>
        <KpiCard><div className="kpi-label">Pendentes</div><div className="kpi-value">{kpis?.pendentes ?? '—'}</div></KpiCard>
        <KpiCard><div className="kpi-label">Aguardando Aprovação</div><div className="kpi-value">{kpis?.aguardando ?? '—'}</div></KpiCard>
        <KpiCard><div className="kpi-label">Aprovadas (a importar)</div><div className="kpi-value">{kpis?.aprovadasAImportar ?? '—'}</div></KpiCard>
        <KpiCard><div className="kpi-label">Importadas</div><div className="kpi-value">{kpis?.importadas ?? '—'}</div></KpiCard>
      </KpiGrid>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Atendimento Presencial</div>
          <Bar label="Com atendimento" value={charts?.atendimento.com ?? 0} total={kpis?.total ?? 0} color="#10B981" onClick={() => aplicarRapido('atendimento', '1')} />
          <Bar label="Sem atendimento" value={charts?.atendimento.sem ?? 0} total={kpis?.total ?? 0} color="#9CA3AF" onClick={() => aplicarRapido('atendimento', '0')} />
          {(charts?.atendimento.aDefinir ?? 0) > 0 && (
            <Bar label="A definir" value={charts?.atendimento.aDefinir ?? 0} total={kpis?.total ?? 0} color="#FCD34D" />
          )}
        </Card>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Tipo de Evento</div>
          <Bar label="🏙️ Local" value={charts?.tipo.local ?? 0} total={kpis?.total ?? 0} color="#3B82F6" onClick={() => aplicarRapido('tipo', 'LOCAL')} />
          <Bar label="✈️ Viagem" value={charts?.tipo.viagem ?? 0} total={kpis?.total ?? 0} color="#F59E0B" onClick={() => aplicarRapido('tipo', 'VIAGEM')} />
        </Card>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: BD.grayText }}>Atalhos:</span>
        <Button $variant="secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => aplicarSemana(0)}>Esta semana</Button>
        <Button $variant="secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => aplicarSemana(-1)}>Semana anterior</Button>
      </div>

      <FiltersRow as="form" onSubmit={(e: any) => { e.preventDefault(); aplicar() }}>
        <Field>
          <label>Praça</label>
          <Select value={draft.praca ?? ''} onChange={(e) => setD('praca', e.target.value)}>
            <option value="">Todas</option>
            {pracaOptions.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Status</label>
          <Select value={draft.status ?? ''} onChange={(e) => setD('status', e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(STATUS_UI).map(([key, ui]) => <option key={key} value={key}>{ui.label}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Atendimento</label>
          <Select value={draft.atendimento ?? ''} onChange={(e) => setD('atendimento', e.target.value)}>
            <option value="">Todos</option>
            <option value="1">Com atendimento</option>
            <option value="0">Sem atendimento</option>
          </Select>
        </Field>
        <Field>
          <label>Tipo de Evento</label>
          <Select value={draft.tipo ?? ''} onChange={(e) => setD('tipo', e.target.value)}>
            <option value="">Todos</option>
            <option value="LOCAL">Local</option>
            <option value="VIAGEM">Viagem</option>
          </Select>
        </Field>
        <Field>
          <label>Nome do Evento</label>
          <Input value={draft.evento ?? ''} onChange={(e) => setD('evento', e.target.value)} placeholder="Buscar..." />
        </Field>
        <Field>
          <label>Produtor</label>
          <Input value={draft.produtor ?? ''} onChange={(e) => setD('produtor', e.target.value)} placeholder="Buscar..." />
        </Field>
        <Field>
          <label>Responsável</label>
          <Input value={draft.responsavel ?? ''} onChange={(e) => setD('responsavel', e.target.value)} placeholder="Buscar..." />
        </Field>
        <Field>
          <label>Data inicial</label>
          <Input type="date" value={draft.dataIni ?? ''} onChange={(e) => setD('dataIni', e.target.value)} />
        </Field>
        <Field>
          <label>Data final</label>
          <Input type="date" value={draft.dataFim ?? ''} onChange={(e) => setD('dataFim', e.target.value)} />
        </Field>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <Button type="submit">Filtrar</Button>
          <Button type="button" $variant="secondary" onClick={limpar}>Limpar</Button>
        </div>
      </FiltersRow>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <Table>
          <thead>
            <tr>
              {canImport && (
                <th style={{ width: 40 }}>
                  <input type="checkbox" checked={allSel} onChange={toggleAll} disabled={selecionaveis.length === 0} />
                </th>
              )}
              <SortableTh col="numero" sortBy={applied.sortBy} sortDir={applied.sortDir} onSort={ordenar}>Nº OS</SortableTh>
              <SortableTh col="evento" sortBy={applied.sortBy} sortDir={applied.sortDir} onSort={ordenar}>Evento</SortableTh>
              <SortableTh col="praca" sortBy={applied.sortBy} sortDir={applied.sortDir} onSort={ordenar}>Praça</SortableTh>
              <SortableTh col="produtor" sortBy={applied.sortBy} sortDir={applied.sortDir} onSort={ordenar}>Produtor</SortableTh>
              <SortableTh col="dataTermino" sortBy={applied.sortBy} sortDir={applied.sortDir} onSort={ordenar}>Data Término</SortableTh>
              <th title="Posição deste evento entre os eventos do produtor, ordenados por data">Evento do produtor</th>
              <SortableTh col="responsavel" sortBy={applied.sortBy} sortDir={applied.sortDir} onSort={ordenar}>Responsável</SortableTh>
              <SortableTh col="atendimento" sortBy={applied.sortBy} sortDir={applied.sortDir} onSort={ordenar}>Atendimento</SortableTh>
              <SortableTh col="status" sortBy={applied.sortBy} sortDir={applied.sortDir} onSort={ordenar}>Status</SortableTh>
              <SortableTh col="importacao" sortBy={applied.sortBy} sortDir={applied.sortDir} onSort={ordenar}>Importação</SortableTh>
              <SortableTh col="fechamento" sortBy={applied.sortBy} sortDir={applied.sortDir} onSort={ordenar}>Fechamento</SortableTh>
              <SortableTh col="total" sortBy={applied.sortBy} sortDir={applied.sortDir} onSort={ordenar} alignRight>Total</SortableTh>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={cols} style={{ textAlign: 'center', padding: 48 }}>
                <Spinner />
                <div style={{ marginTop: 12, color: BD.grayText }}>Carregando ordens de serviço…</div>
              </td></tr>
            )}
            {!isLoading && isError && (
              <tr><td colSpan={cols} style={{ textAlign: 'center', padding: 40, color: '#991B1B' }}>Erro ao carregar. Tente novamente.</td></tr>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <tr><td colSpan={cols} style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>📭 Nenhuma OS encontrada com esses filtros.</td></tr>
            )}
            {!isLoading && !isError && rows.map((os) => {
              const ui = STATUS_UI[os.status]
              return (
                <tr key={os.id} onClick={() => navigate(`/interno/os/${os.id}`)}>
                  {canImport && (
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                      {importavel(os) && (
                        <input type="checkbox" checked={selected.has(os.id)} onChange={() => toggleOne(os.id)} />
                      )}
                    </td>
                  )}
                  <td><span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{os.numero}</span></td>
                  <td style={{ fontWeight: 500 }}>{os.evento ?? '—'}</td>
                  <td><Pill $bg="#F3F4F6" $color="#4B5563">{os.praca ?? '—'}</Pill></td>
                  <td>{os.produtor ?? '—'}</td>
                  <td>{fmtDate(os.dataTermino)}</td>
                  <td>
                    {os.produtorEventoRank && os.produtorTotalEventos ? (
                      (() => {
                        const ultimo = os.produtorEventoRank === os.produtorTotalEventos
                        return (
                          <Pill
                            $bg={ultimo ? '#FEE2E2' : '#EEF2FF'}
                            $color={ultimo ? '#991B1B' : '#3730A3'}
                            title={
                              ultimo
                                ? `${os.produtor ?? 'Produtor'}: ÚLTIMO evento (${os.produtorEventoRank} de ${os.produtorTotalEventos}, por data)`
                                : `${os.produtor ?? 'Produtor'}: evento ${os.produtorEventoRank} de ${os.produtorTotalEventos} (por data)`
                            }
                            style={ultimo ? { fontWeight: 700 } : undefined}
                          >
                            {ultimo && '🏁 '}
                            {os.produtorEventoRank}/{os.produtorTotalEventos}
                          </Pill>
                        )
                      })()
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>—</span>
                    )}
                  </td>
                  <td style={{ color: BD.grayText }}>{os.responsavel ?? '—'}</td>
                  <td>
                    {os.temAtendimento === null ? (
                      <span style={{ color: '#9CA3AF' }}>—</span>
                    ) : os.temAtendimento ? (
                      <Pill $bg="#D1FAE5" $color="#065F46">Sim</Pill>
                    ) : (
                      <Pill $bg="#F3F4F6" $color="#4B5563">Não</Pill>
                    )}
                  </td>
                  <td><StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></td>
                  <td>
                    {os.importStatus === 'IMPORTADA' ? (
                      <StatusBadge $bg="#DBEAFE" $color="#1E3A8A">Importada</StatusBadge>
                    ) : os.importStatus === 'PARCIAL' ? (
                      <StatusBadge $bg="#FEF3C7" $color="#92400E">Parcial</StatusBadge>
                    ) : os.status === 'APROVADA' ? (
                      <StatusBadge $bg="#FED7AA" $color="#9A3412">Pendente</StatusBadge>
                    ) : (
                      <span style={{ color: '#D1D5DB' }}>—</span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {os.fechamentoStatus ? (
                        <StatusBadge
                          $bg={FECHAMENTO_UI[os.fechamentoStatus]?.bg ?? '#F3F4F6'}
                          $color={FECHAMENTO_UI[os.fechamentoStatus]?.color ?? '#4B5563'}
                        >
                          {FECHAMENTO_UI[os.fechamentoStatus]?.label ?? os.fechamentoStatus}
                        </StatusBadge>
                      ) : (
                        <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>
                      )}
                      <a
                        href={p2FechamentoUrl(os.eventoId)}
                        target="_blank"
                        rel="noreferrer"
                        title="Abrir fechamento no P2"
                        style={{ textDecoration: 'none', fontSize: 14 }}
                      >
                        🔗
                      </a>
                    </div>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(os.custoTotal)}</td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
        <Button $variant="secondary" disabled>Exportar Excel</Button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button $variant="secondary" disabled={paginaAtual <= 1} onClick={() => irPagina(paginaAtual - 1)}>‹ Anterior</Button>
          <span style={{ fontSize: 13, color: BD.grayText }}>Página {paginaAtual}</span>
          <Button $variant="secondary" disabled={!!lastPage} onClick={() => irPagina(paginaAtual + 1)}>Próxima ›</Button>
        </div>
      </div>

      {confirmOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setConfirmOpen(false)}
        >
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, padding: 24 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Importar {selecionadas.length} OS para o Fechamento?</h3>
            <p style={{ fontSize: 13, color: BD.grayText, marginBottom: 14 }}>
              As OS abaixo serão lançadas como custos no fechamento (P2). A operação registra no log de cada OS e muda o status para <strong>Importada</strong>.
            </p>
            <div style={{ background: BD.grayBg, borderRadius: 8, padding: 10, marginBottom: 14, maxHeight: 260, overflowY: 'auto' }}>
              {selecionadas.map((r) => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: `1px solid ${BD.grayBorder}` }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>OS {r.numero}</div>
                    <div style={{ fontSize: 12, color: BD.grayText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.evento ?? '—'}</div>
                  </div>
                  <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMoney(r.custoTotal)}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: BD.redLight, borderRadius: 8, padding: 12, marginBottom: 16 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>Total a importar:</span>
              <span style={{ color: BD.red, fontWeight: 800, fontSize: 18 }}>{fmtMoney(totalSel)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button $variant="secondary" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
              <Button onClick={iniciarImportacao}>Iniciar importação</Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default OsList
